/**
 * Whether a media query currently matches, kept live as the viewport changes.
 *
 * SSR-safe and test-safe: without `matchMedia` the answer is false, which is
 * the desktop shell, so a test that does not care about width gets the full
 * rail and no drawer.
 */
import { useCallback, useSyncExternalStore } from 'react';

function noop(): void {}

/** True while `query` matches; false where `matchMedia` is unavailable. */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return noop;
      const list = window.matchMedia(query);
      list.addEventListener('change', onChange);
      return () => list.removeEventListener('change', onChange);
    },
    [query],
  );
  const getSnapshot = useCallback(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia(query).matches;
  }, [query]);
  return useSyncExternalStore(subscribe, getSnapshot, () => false);
}
